import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronRight, Database, Globe, HardDrive, Link2 } from 'lucide-react';
import apiService from '../services/api';
import { useToast } from '../context/ToastContext';
import DBCredentialsCore from '../components/DBCredentialsCore';
import EgressIPsCore from '../components/EgressIPsCore';
import OdbcDriversCore from '../components/OdbcDriversCore';
import OdbcConnectionsCore from '../components/OdbcConnectionsCore';
import { useJobPoller } from '../hooks/useJobPoller';
import {
  DBCredentials,
  OdbcCatalogDriver,
  OdbcDsn,
  OdbcCredential,
  OdbcInstalledDriver,
  OdbcJobStatus,
  TierLimitInfo,
  EgressIp,
  DsnTemplateField,
} from '../widget/types';

type TabKey = 'internal' | 'egress' | 'drivers' | 'connections';

const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'internal', label: 'Internal Database', icon: <Database size={14} /> },
  { key: 'egress', label: 'Firewall Allowlist', icon: <Globe size={14} /> },  
  { key: 'drivers', label: 'ODBC Drivers', icon: <HardDrive size={14} /> },
  { key: 'connections', label: 'ODBC Connections', icon: <Link2 size={14} /> },
];

const DataConnectionsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const workspaceId = parseInt(id || '0', 10);
  const { showSuccess, showError } = useToast();

  const [activeTab, setActiveTab] = useState<TabKey>('internal');
  const [workspace, setWorkspace] = useState<any>(null);

  // Internal DB state
  const [credentials, setCredentials] = useState<DBCredentials | null>(null);
  const [credLoading, setCredLoading] = useState(true);

  // Egress state
  const [egressIps, setEgressIps] = useState<EgressIp[]>([]);
  const [egressLoading, setEgressLoading] = useState(true);
  const [egressError, setEgressError] = useState<string | null>(null);

  // Driver state
  const [catalog, setCatalog] = useState<OdbcCatalogDriver[]>([]);
  const [tierLimit, setTierLimit] = useState<TierLimitInfo>({ current: 0, limit: 0, unlimited: false });
  const [canTier2, setCanTier2] = useState(false);
  const [driverLoading, setDriverLoading] = useState(true);
  const [driverError, setDriverError] = useState<string | null>(null);

  // Connection state
  const [dsns, setDsns] = useState<OdbcDsn[]>([]);
  const [odbcCredentials, setOdbcCredentials] = useState<OdbcCredential[]>([]);
  const [installedDrivers, setInstalledDrivers] = useState<OdbcInstalledDriver[]>([]);
  const [dsnTemplates, setDsnTemplates] = useState<Record<string, Record<string, DsnTemplateField>>>({});
  const [connLoading, setConnLoading] = useState(true);
  const [connError, setConnError] = useState<string | null>(null);

  // Job pollers
  const pollJobApi = async (jobId: number): Promise<OdbcJobStatus> => {
    const res = await apiService.post(`/workspaces/${workspaceId}/odbc/jobs/${jobId}`, {});
    return res.data.job;
  };

  const driverPoller = useJobPoller(pollJobApi, {
    onComplete: () => { loadCatalog(); },
    onFailed: (job) => { showError(job.error_message || 'Driver job failed'); loadCatalog(); },
  });

  const connPoller = useJobPoller(pollJobApi, {
    onComplete: () => { loadConnections(); },
    onFailed: (job) => { showError(job.error_message || 'Connection job failed'); loadConnections(); },
  });

  // Loaders
  const loadCredentials = async () => {
    setCredLoading(true);
    try {
      const res = await apiService.get(`/workspaces/${workspaceId}/credentials`);
      setCredentials(res.data);
    } catch (err: any) {
      showError(err.message || 'Failed to load credentials');
    } finally {
      setCredLoading(false);
    }
  };

  const loadEgressIps = async () => {
    setEgressLoading(true);
    setEgressError(null);
    try {
      const res = await apiService.get(`/workspaces/${workspaceId}/odbc/egress-ips`);
      setEgressIps(res.data.ips || []);
    } catch (err: any) {
      setEgressError(err.message || 'Failed to load egress IPs');
    } finally {
      setEgressLoading(false);
    }
  };

  const loadCatalog = async () => {
    setDriverLoading(true);
    setDriverError(null);
    try {
      const res = await apiService.get(`/workspaces/${workspaceId}/odbc/catalog`);
      setCatalog(res.data.catalog || []);
      setTierLimit(res.data.tierLimit || { current: 0, limit: 0, unlimited: false });
      setCanTier2(res.data.canTier2 || false);
    } catch (err: any) {
      setDriverError(err.message || 'Failed to load catalog');
    } finally {
      setDriverLoading(false);
    }
  };

  const loadConnections = async () => {
    setConnLoading(true);
    setConnError(null);
    try {
      const [dsnRes, credRes, driverRes, ipRes] = await Promise.all([
        apiService.get(`/workspaces/${workspaceId}/odbc/dsns`),
        apiService.get(`/workspaces/${workspaceId}/odbc/credentials`),
        apiService.get(`/workspaces/${workspaceId}/odbc/drivers`),
        apiService.get(`/workspaces/${workspaceId}/odbc/egress-ips`),
      ]);
      setDsns(dsnRes.data.dsns || []);
      setDsnTemplates(dsnRes.data.dsnTemplates || {});
      setOdbcCredentials(credRes.data.credentials || []);
      setInstalledDrivers(driverRes.data.drivers || []);
    } catch (err: any) {
      setConnError(err.message || 'Failed to load connections');
    } finally {
      setConnLoading(false);
    }
  };

  useEffect(() => {
    apiService.get(`/workspaces/${workspaceId}`).then(res => setWorkspace(res.data)).catch(() => {});
  }, [workspaceId]);

  useEffect(() => {
    if (activeTab === 'internal') loadCredentials();
    if (activeTab === 'egress') loadEgressIps();
    if (activeTab === 'drivers') loadCatalog();
    if (activeTab === 'connections') loadConnections();
  }, [activeTab, workspaceId]);

  const tabStyle = (isActive: boolean): React.CSSProperties => ({
    padding: '10px 18px',
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: isActive ? 'var(--v2-primary)' : 'var(--v2-text-tertiary)',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: isActive ? '2px solid var(--v2-primary)' : '2px solid transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'all 0.15s ease',
  });

  return (
    <div
      style={{
        padding: '32px',
        backgroundColor: 'var(--v2-bg)',
        minHeight: '100vh',
        fontFamily: 'var(--font-sans)',
      }}
    >
      {/* Breadcrumbs */}
      <nav
        style={{
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '0.875rem',
        }}
      >
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            fontSize: '0.875rem',
            color: 'var(--v2-text-tertiary)',
            fontWeight: 500,
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--v2-primary)')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--v2-text-tertiary)')}
        >
          Workspaces
        </button>
        <ChevronRight size={14} strokeWidth={1.5} color="var(--v2-text-muted)" />
	<span style={{ fontSize: '0.875rem', color: 'var(--v2-text)', fontWeight: 500 }}>
          {workspace?.name}
        </span>
        <ChevronRight size={14} strokeWidth={1.5} color="var(--v2-text-muted)" />
        <span style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)' }}>
          Data Connections
        </span>
      </nav>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--v2-border)', marginBottom: '24px' }}>
        {TABS.map(tab => (
          <button key={tab.key} style={tabStyle(activeTab === tab.key)} onClick={() => setActiveTab(tab.key)}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'internal' && (
        <DBCredentialsCore
          credentials={credentials}
          loading={credLoading}
          error={null}
          mode="modal"
          onRetry={loadCredentials}
          onCopySuccess={showSuccess}
          onCopyError={showError}
        />
      )}

      {activeTab === 'egress' && (
        <EgressIPsCore
          ips={egressIps}
          loading={egressLoading}
          error={egressError}
          mode="modal"
          onCopySuccess={showSuccess}
          onCopyError={showError}
        />
      )}

      {activeTab === 'drivers' && (
        <OdbcDriversCore
          catalog={catalog}
          tierLimit={tierLimit}
          canTier2={canTier2}
          loading={driverLoading}
          error={driverError}
          activeJobDriverKey={null}
          activeJobStatus={driverPoller.activeJob?.status || null}
          mode="modal"
	  onInstallDriver={async (driverKey, downloadUrl) => {
            const body: any = { driver_key: driverKey };
            if (downloadUrl) body.download_url = downloadUrl;
            const res = await apiService.post(`/workspaces/${workspaceId}/odbc/drivers/install`, body);
            driverPoller.startPolling(res.data.job_id);
          }}
          onUninstallDriver={async (driverKey) => {
            const res = await apiService.post(`/workspaces/${workspaceId}/odbc/drivers/uninstall`, { driver_key: driverKey });
            driverPoller.startPolling(res.data.job_id);
          }}
          onRefresh={loadCatalog}
          onSuccess={showSuccess}
          onError={showError}
        />
      )}

      {activeTab === 'connections' && (
        <OdbcConnectionsCore
          dsns={dsns}
          credentials={odbcCredentials}
          installedDrivers={installedDrivers}
          dsnTemplates={dsnTemplates}
          egressIps={egressIps}
          loading={connLoading}
          error={connError}
          activeJobDsnId={null}
          activeJobStatus={connPoller.activeJob?.status || null}
          mode="modal"
          onCreateDsn={async (data) => {
            const res = await apiService.post(`/workspaces/${workspaceId}/odbc/dsns/create`, data);
            connPoller.startPolling(res.data.job_id);
          }}
          onUpdateDsn={async (id, data) => {
            const res = await apiService.post(`/workspaces/${workspaceId}/odbc/dsns/${id}/update`, data);
            connPoller.startPolling(res.data.job_id);
          }}
          onDeleteDsn={async (id) => {
            const res = await apiService.post(`/workspaces/${workspaceId}/odbc/dsns/${id}/delete`, {});
            connPoller.startPolling(res.data.job_id);
          }}
          onTestDsn={async (id, username, password) => {
            const res = await apiService.post(`/workspaces/${workspaceId}/odbc/dsns/${id}/test`, { username, password });
            connPoller.startPolling(res.data.job_id);
          }}
	 onUploadCredential={async (file, name, type, dsnName) => {
            const reader = new FileReader();
            const b64 = await new Promise<string>((resolve, reject) => {
              reader.onload = () => {
                const result = reader.result as string;
                resolve(result.split(',')[1]);
              };
              reader.onerror = () => reject(new Error('Failed to read file'));
              reader.readAsDataURL(file);
            });
            const body: any = {
              credential_name: name,
              credential_type: type,
              file_content_b64: b64,
              file_name: file.name,
            };
            if (dsnName) body.dsn_name = dsnName;
            const res = await apiService.post(`/workspaces/${workspaceId}/odbc/credentials/upload`, body);
            connPoller.startPolling(res.data.job_id);
          }}
          onDeleteCredential={async (id) => {
            const res = await apiService.post(`/workspaces/${workspaceId}/odbc/credentials/${id}/delete`, {});
            connPoller.startPolling(res.data.job_id);
          }}
          onRefresh={loadConnections}
          onSuccess={showSuccess}
          onError={showError}
        />
      )}
    </div>
  );
};

export default DataConnectionsPage;
